import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '@app/services/api.base.service';
import { environment } from '@env/environment.local';

export interface RegionDrmCatalogItem {
  id: number;
  regionName: string;
  segment: 'CARGA' | 'AUTOBUSES';
  drmUserId: number;
  drmUserName: string;
  drmEmail?: string;
  branchCount: number;
  enabled: boolean;
}

export interface RegionDrmCatalogDetail {
  id: number;
  regionLabel: string;
  regionName: string;
  regionNumber: number;
  segment: 'CARGA' | 'AUTOBUSES';
  drmUserId: number;
  drmUserName: string;
  dealerBranchIds: number[];
  enabled: boolean;
}

export interface RegionDrmUserOption {
  id: number;
  label: string;
  email?: string;
}

export interface RegionDrmBranchOption {
  id: number;
  name: string;
  selectable: boolean;
  assignedRegionLabel?: string;
}

export interface RegionDrmCatalogQuery {
  segment?: string | null;
  regionName?: string | null;
}

export interface CreateRegionDrmPayload {
  segment: 'CARGA' | 'AUTOBUSES';
  regionNumber: number;
  drmUserId: number;
  dealerBranchIds: number[];
  enabled?: boolean;
}

export interface UpdateRegionDrmPayload {
  drmUserId: number;
  dealerBranchIds: number[];
  enabled?: boolean;
}

@Injectable({ providedIn: 'root' })
export class RegionDrmCatalogService {
  private readonly baseUrl = `${environment.API_CREDIT_REQUEST_SERVICE}/cat-drm-catalog`;

  constructor(private readonly api: ApiService) {}

  list(query: RegionDrmCatalogQuery): Observable<{ success: boolean; data: RegionDrmCatalogItem[] }> {
    const params = new URLSearchParams();
    if (query.segment) {
      params.set('segment', query.segment);
    }
    if (query.regionName) {
      params.set('regionName', query.regionName);
    }
    const qs = params.toString();
    const url = qs ? `${this.baseUrl}?${qs}` : this.baseUrl;
    return this.api.get(url);
  }

  getById(id: number): Observable<{ success: boolean; data: RegionDrmCatalogDetail }> {
    return this.api.get(`${this.baseUrl}/${id}`);
  }

  create(payload: CreateRegionDrmPayload): Observable<{ success: boolean; data: RegionDrmCatalogDetail }> {
    return this.api.post(this.baseUrl, payload);
  }

  update(
    id: number,
    payload: UpdateRegionDrmPayload,
  ): Observable<{ success: boolean; data: RegionDrmCatalogDetail }> {
    return this.api.put(`${this.baseUrl}/${id}`, payload);
  }

  delete(id: number): Observable<{ success: boolean }> {
    return this.api.deleteWithoutBody(`${this.baseUrl}/${id}`);
  }

  listDrmUsers(
    onlySalesManager = true,
  ): Observable<{ success: boolean; data: RegionDrmUserOption[] }> {
    return this.api.get(
      `${this.baseUrl}/drm-users?onlySalesManager=${onlySalesManager}`,
    );
  }

  listDealerBranches(
    segment: string,
    excludeCatDrmId?: number,
  ): Observable<{ success: boolean; data: RegionDrmBranchOption[] }> {
    let url = `${this.baseUrl}/dealer-branches?segment=${segment}`;
    if (excludeCatDrmId != null) {
      url += `&excludeCatDrmId=${excludeCatDrmId}`;
    }
    return this.api.get(url);
  }
}
